// This composable is used to set up instances of Donut Selectors, be they static/dynamic or single/multi.
// The logic herein is in two main parts
// 1) Menu Positioning:
// // This is all derived from the useSelectorPositioning composable. See that file for specifics.
// 2) Selection Propagation and Rendering
// // We provide a keyMap for options that don't subscribe to standard 'value', 'label', 'description' nomenclature
// // and add a watcher to detect when each component's `selection` value has changed, updating it in the parent
import { useStore } from 'vuex';
import { computed, watch, onUnmounted } from 'vue';
import useSelectorPositioning from 'composables/selector/positioning';

const useBaseSelector = ({
 props, emit, selectorEl, selection, paywall,
}) => {
  const { getters, dispatch } = useStore();

  // Menu Positioning
  const {
    onSelectorButtonClick,
    selectorMenuIsOpen,
    selectorMenuStyle,
    calculateSelectorMenuPosition,
    closeSelectorMenu,
  } = useSelectorPositioning({ selectorEl, paywall });

  // Selection Monitoring
  watch(() => props.modelValue, (newValue) => {
      // There may be locations where the v-model of a selector is changed by a different
      // UI element elsewhere on the page. Watch for this and update accordingly.
      selection.value = newValue;
    });

  // Selection Rendering
  const keyMap = computed(() => {
    // keyMap is for formatting options to render correct in the menu and selection fields
    // For instance, if a User object was coming through and we wanted to pass its ID to a
    // controller, render its name in the selector component, and show its email as an added description,
    // the keyMap object might look like { value: 'id', label: 'name', description: 'email' }
    const defaults = { value: 'value', label: 'label', description: 'description' };
    return { ...defaults, ...props.keyMap };
  });
  const propagateNewSelection = (newSelection) => {
    // The default here is to just emit update:modelValue, but in some instances (for instance, if the value
    // is in VueX state) we do not want to just update the value as it is provided. We may want to call a function
    // or dispatch an action with the new values. In this case, we would not be emitting and directly updating a new value
    // on the parent component, but it would be updated via the alternate functionality.
    if (props.updateFunction) {
      props.updateFunction(newSelection);
    } else if (props.updateAction) {
      dispatch(props.updateAction, newSelection);
    } else {
      emit('update:modelValue', newSelection);
    }
  };
  watch(selection, (newValue) => {
    propagateNewSelection(newValue);
  });

  // Paywall
  const upgradeRequired = computed(() => getters.upgradeRequired(paywall));

  // Lifecycle Hooks
  onUnmounted(() => {
    window.removeEventListener('click', closeSelectorMenu);
    window.removeEventListener('onScroll', calculateSelectorMenuPosition);
  });

  return {
    onSelectorButtonClick,
    selectorMenuIsOpen,
    selectorMenuStyle,
    calculateSelectorMenuPosition,
    upgradeRequired,
    keyMap,
  };
};

export default useBaseSelector;
